import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Database, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminApiDebug = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedMatch, setExpandedMatch] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/api-football-matches`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000 // 3 minutes - fetching odds can be slow
      });
      setData(response.data);
      toast.success(`Loaded ${response.data.total_matches} matches`);
    } catch (err) {
      console.error('Error fetching API data:', err);
      setError(err.response?.data?.detail || err.message);
      toast.error('Failed to fetch API data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Card className="glass-dark border-red-500/30">
              <CardContent className="pt-6">
                <div className="text-center py-8 text-red-400">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-4" />
                  <h2 className="text-xl font-bold">Admin Access Required</h2>
                  <p className="mt-2">This page is only accessible to administrators.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <Database className="w-8 h-8 text-blue-400" />
                API-Football Debug
              </h1>
              <p className="text-gray-400 mt-1">
                View all available matches and betting markets from API-Football
              </p>
            </div>
            <Button
              onClick={fetchData}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </>
              )}
            </Button>
          </div>

          {/* Error */}
          {error && (
            <Card className="glass-dark border-red-500/30 mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 text-red-400">
                  <XCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading */}
          {loading && !data && (
            <Card className="glass-dark border-white/10">
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <RefreshCw className="w-12 h-12 text-blue-400 mx-auto animate-spin mb-4" />
                  <p className="text-gray-400">Fetching matches from API-Football...</p>
                  <p className="text-gray-500 text-sm mt-2">This may take 1-2 minutes</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          {data && (
            <>
              <Card className="glass-dark border-white/10 mb-6">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-white">{data.total_matches}</div>
                      <div className="text-sm text-gray-400">Total Matches</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-400">
                        {data.summary?.matches_with_singles_options || 0}
                      </div>
                      <div className="text-sm text-gray-400">Singles Range</div>
                      <div className="text-xs text-gray-500">{data.singles_range}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-400">
                        {data.summary?.matches_with_doubles_options || 0}
                      </div>
                      <div className="text-sm text-gray-400">Doubles Range</div>
                      <div className="text-xs text-gray-500">{data.doubles_range}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-purple-400">
                        {data.summary?.matches_with_trebles_options || 0}
                      </div>
                      <div className="text-sm text-gray-400">Trebles Range</div>
                      <div className="text-xs text-gray-500">{data.trebles_range}</div>
                    </div>
                  </div>

                  {/* Can Create */}
                  <div className="flex flex-wrap gap-4">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                      data.summary?.can_create_singles ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {data.summary?.can_create_singles ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      <span>Singles (need 3)</span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                      data.summary?.can_create_double ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {data.summary?.can_create_double ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      <span>Double (need 2 more)</span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                      data.summary?.can_create_treble ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {data.summary?.can_create_treble ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      <span>Treble (need 3 more)</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-4">
                    {data.summary?.note}
                  </p>
                </CardContent>
              </Card>

              {/* Matches List */}
              <Card className="glass-dark border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">
                    All Matches ({data.matches?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {data.matches?.map((match, index) => (
                      <div 
                        key={match.id || index}
                        className="bg-white/5 rounded-lg p-4 border border-white/10"
                      >
                        <div 
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 cursor-pointer"
                          onClick={() => setExpandedMatch(expandedMatch === index ? null : index)}
                        >
                          <div>
                            <div className="text-xs text-blue-400 mb-1">
                              {match.country} - {match.league}
                            </div>
                            <div className="text-white font-semibold">
                              {match.home_team} vs {match.away_team}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(match.start_time).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <span className="px-2 py-1 text-xs rounded bg-white/10 text-gray-300">
                              {match.total_markets} markets
                            </span>
                            {match.singles_markets?.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400">
                                {match.singles_markets.length} singles
                              </span>
                            )}
                            {match.doubles_markets?.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400">
                                {match.doubles_markets.length} doubles
                              </span>
                            )}
                            {match.trebles_markets?.length > 0 && (
                              <span className="px-2 py-1 text-xs rounded bg-purple-500/20 text-purple-400">
                                {match.trebles_markets.length} trebles
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedMatch === index && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            {/* Basic Odds */}
                            <div className="mb-4">
                              <div className="text-sm text-gray-400 mb-2">Match Winner Odds:</div>
                              <div className="flex gap-4">
                                <span className="px-3 py-1 bg-white/10 rounded text-white">
                                  Home: {match.home_odds}
                                </span>
                                <span className="px-3 py-1 bg-white/10 rounded text-white">
                                  Draw: {match.draw_odds}
                                </span>
                                <span className="px-3 py-1 bg-white/10 rounded text-white">
                                  Away: {match.away_odds}
                                </span>
                              </div>
                            </div>

                            {/* Singles Markets */}
                            {match.singles_markets?.length > 0 && (
                              <div className="mb-4">
                                <div className="text-sm text-green-400 mb-2">
                                  Singles Range (1.30-2.00): {match.singles_markets.length} markets
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {match.singles_markets.map((m, i) => (
                                    <span key={i} className="px-2 py-1 text-xs bg-green-500/20 text-green-300 rounded">
                                      {m.market}: {m.odds}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Doubles Markets */}
                            {match.doubles_markets?.length > 0 && (
                              <div className="mb-4">
                                <div className="text-sm text-blue-400 mb-2">
                                  Doubles Range (1.45-1.70): {match.doubles_markets.length} markets
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {match.doubles_markets.map((m, i) => (
                                    <span key={i} className="px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded">
                                      {m.market}: {m.odds}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Trebles Markets */}
                            {match.trebles_markets?.length > 0 && (
                              <div className="mb-4">
                                <div className="text-sm text-purple-400 mb-2">
                                  Trebles Range (1.27-1.45): {match.trebles_markets.length} markets
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {match.trebles_markets.map((m, i) => (
                                    <span key={i} className="px-2 py-1 text-xs bg-purple-500/20 text-purple-300 rounded">
                                      {m.market}: {m.odds}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* No Markets Warning */}
                            {!match.singles_markets?.length && !match.doubles_markets?.length && !match.trebles_markets?.length && (
                              <div className="text-yellow-400 text-sm flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                No markets in prediction ranges for this match
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {(!data.matches || data.matches.length === 0) && (
                      <div className="text-center py-8 text-gray-400">
                        <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No matches with odds data available</p>
                        <p className="text-sm mt-2">This could mean API-Football has no odds for today's fixtures yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminApiDebug;
